import axios from "axios";
import route from "ziggy-js";
import { computed, isRef } from "vue";
import { router } from "@inertiajs/vue3";
import { validate as isUuid4 } from "uuid";
import { toast } from "@/SharedComponents/Toaster";
import { useWebView } from "@/Composables/useWebView";
import { useI18n } from "vue-i18n";

export function useQrCodeScanner(
    item = null,
    // eslint-disable-next-line no-unused-vars
    onUpdate = (_) => null
) {
    const _item = computed(() => (isRef(item) ? item.value : item));

    const { t } = useI18n();

    const { runningInWebView, getWebViewBridge } = useWebView();

    function openQrCodeScanner() {
        if (!runningInWebView()) {
            return;
        }

        getWebViewBridge().qrScannerOpenModal({
            title: t("admin.scan_qr_code.title"),
            invalid_qr_code: "Invalid QR code",
            back_button_text: t("components.buttons.back"),
        });
    }

    function dismissQrCodeScanner() {
        if (!runningInWebView()) {
            return;
        }

        getWebViewBridge().qrScannerDismissModal();
    }

    async function handleWebviewQrCodeScanned(e, callback = async () => null) {
        dismissQrCodeScanner();
        await callback(e.detail.id);
    }

    async function verifyQrCode(id) {
        id = await parseId(id);

        const {
            data: { assigned, space, id: uuid },
        } = await axios.get(route("qr_code.verify", id));

        return {
            assigned,
            space,
            uuid,
        };
    }

    async function updateQrCode(e) {
        await handleWebviewQrCodeScanned(e, _updateQrCode);
    }

    async function _updateQrCode(id) {
        if (!_item.value) {
            return;
        }

        id = await parseId(id);

        try {
            return new Promise((resolve, reject) => {
                router.put(
                    route("qr_code.update", id),
                    { space_id: _item.value.id },
                    {
                        onSuccess: (response) => {
                            toast.success(
                                t("toasts.qr_code.assign_space.success")
                            );
                            onUpdate(getItem(response));
                            resolve(response);
                        },
                        onError: (error) => {
                            toast.error(t("toasts.qr_code.assign_space.error"));
                            reject(error);
                        },
                    }
                );
            });
        } catch (error) {
            return Promise.reject(error);
        }
    }

    function getItem(data) {
        if (data.props.asset) {
            return data.props.asset;
        }
        if (data.props.space) {
            return data.props.space;
        }
        return {};
    }

    async function parseId(id) {
        if (isUuid4(id)) {
            return id;
        }

        // If something other than a UUID is decoded, we assume a QR code
        // with a shortened URL was scanned.
        const {
            data: { url },
        } = await axios.get(route("s.resolve", id));

        return url.split("/").pop();
    }

    return {
        runningInWebView,
        getWebViewBridge,
        openQrCodeScanner,
        dismissQrCodeScanner,
        handleWebviewQrCodeScanned,
        verifyQrCode,
        updateQrCode,
        _updateQrCode,
        getItem,
        parseId,
    };
}
